import { useState, useEffect, useCallback, useRef, useMemo } from 'react'

import { useLazyQuery } from '@apollo/client'
import { Menu } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import cloneDeep from 'lodash.clonedeep'
import { Plus } from 'lucide-react'

import { navigate, useLocation } from '@redwoodjs/router'
import { Metadata, useMutation } from '@redwoodjs/web'
import { useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import Tag from 'src/components/DataDisplay/Tag/Tag'
import { CollapsibleSearchBox } from 'src/components/Inputs/CollapsibleSearchBox/CollapsibleSearchBox'
import PaginationArea from 'src/components/Navigation/PaginationArea/PaginationArea'
import ColumnDisplayMenu from 'src/components/Overlays/ColumnDisplayMenu'
import SortByMenu from 'src/components/Overlays/SortByMenu'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useApplicantDrawerContext } from 'src/context/ApplicantDrawerContext'
import { useListOfApplicantsContext } from 'src/context/ApplicantListContext'
import { useCustomSteps } from 'src/context/CustomStepsContext'
import {
  usePhoneDevice,
  usePhoneDeviceDispatch,
} from 'src/context/PhoneDeviceContext'
import {
  GET_APPLICANT_FILTER_PRESETS,
  CREATE_APPLICANT_FILTER_PRESET,
  UPDATE_APPLICANT_FILTER_PRESET,
} from 'src/graphql/applicantFilterPresets.graphql'
import { DOWNLOAD_CSV, GET_APPLICANTS } from 'src/graphql/applicants.graphql'
import { GET_CUSTOM_FIELDS } from 'src/graphql/customFields.graphql'
import { GET_EMPLOYEE_PERMISSIONS } from 'src/graphql/permissions.graphql.tsx'
import { CustomFieldStatus } from 'src/graphql/types/customFields'
import { usePage } from 'src/hooks/usePage/usePage'
import { usePageSize } from 'src/hooks/usePageSize/usePageSize'
import IconChevronDown from 'src/icons/IconChevronDown'
import { isAdminOrOps } from 'src/lib/auth/permissions'
import { PhoneDeviceActionType } from 'src/lib/phone-device.reducer'
import { base64ToBlob, downloadFileBlob } from 'src/lib/string-util'
import FilterPresetList from 'src/pages/ApplicantsPage/components/FilterPresetList/FilterPresetList'
import {
  SelectedFilter,
  SelectedFiltersBar,
} from 'src/pages/ApplicantsPage/components/SelectedFiltersBar/SelectedFiltersBar'

import ApplicantsTable from './components/ApplicantsTable/ApplicantsTable'
import { createColumnConfigs } from './components/ApplicantsTable/columnConfigs'
import { generateCustomFieldColumns } from './components/ApplicantsTable/customFieldColumns'
import { buildStepFiltersVariable } from './components/ApplicantsTable/stepColumnFilters'
import {
  generateStepColumns,
  isStepColumn,
} from './components/ApplicantsTable/stepColumns'
import BulkActionsDrawer from './components/BulkActionsDrawers/BulkActionsDrawerWrapper'
import {
  type BulkActionName,
  type SelectedApplicant,
} from './components/BulkActionsDrawers/bulkActiontypes'
import CreateApplicantDrawer from './components/CreateApplicantDrawer/CreateApplicantDrawer'
import ApplicantFilterMenuWrapper from './components/FilterMenu/ApplicantFilterMenuWrapper'
import FilterPresetModal from './components/FilterPresetModal/FilterPresetModal'
import ScheduleAutomationsModal from './components/ScheduleAutomationsModal/ScheduleAutomationsModal'
import { ApplicantFiltersActionType } from './context/applicant-filters.reducer'
import {
  useFilters,
  useFiltersDispatch,
} from './context/ApplicantFiltersContext'
import { useConditionalDataLoading } from './hooks/useConditionalDataLoading'
import { loadFiltersFromUrl } from './loadFiltersFromUrl'
import { updateFiltersInUrl } from './updateFiltersInUrl'
import { buildPresetColumns, loadColumnsFromPreset } from './utils/columnUtils'
import {
  defaultHiringStages,
  defaultColumnVisibility,
  defaultColumnOrder,
  defaultSortByConfig,
  LIMIT_CSV,
  transformSortConfigForGraphQL,
} from './utils/constants'
import {
  getRequiredDataTypes,
  getRequiredDataTypesFromUrl,
} from './utils/dataLoadingUtils'
import {
  ensureCompleteFilterStructure,
  onRemoveSelectedFilter,
  reset,
} from './utils/filterActionUtils'
import {
  buildFiltersGraphqlParam,
  buildSelectedFilters,
} from './utils/filterUtils'
import {
  mergeOptionsIntoFilters,
  onPresetClick,
  autoUpdatePresetColumns,
  autoUpdatePresetFilters,
  createCustomPresetFromFilters,
  getPresetRequiredDataTypes,
  syncCustomFieldsToOriginalPreset,
  computeHasUnsavedChanges,
  getPresetPageSize,
  DEFAULT_PAGE_SIZE,
} from './utils/presetUtils'

const ApplicantsPage = () => {
  const { currentUser } = useAuth()
  const location = useLocation()

  const [opened, { open, close }] = useDisclosure(false)
  const customStepsCtx = useCustomSteps()
  const [bulkActionsDrawerOpened, bulkActionsDrawerHandlers] =
    useDisclosure(false)
  const [scheduleAutomationsModalOpened, scheduleAutomationsModalHandlers] =
    useDisclosure(false)
  // Internal-only bulk-schedule tool (ENG-4518) — gated to ADMIN/OPS.
  const canScheduleAutomations = isAdminOrOps(currentUser)
  const [page, setPage] = usePage()
  const [pageSize, setPageSize] = usePageSize(10)
  const { handleOpen, refreshKey } = useApplicantDrawerContext()
  const { setApplicantList } = useListOfApplicantsContext()
  const [filterPresetModalOpened, filterPresetModalHandlers] =
    useDisclosure(false)
  const [selectedApplicants, setSelectedApplicants] = useState<
    SelectedApplicant[]
  >([])
  const [activeBulkActionName, setActiveBulkActionName] =
    useState<BulkActionName | null>(null)
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>([])
  const [selectedPresetFilter, setSelectedPresetFilter] = useState()
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null)
  const [originalPresetFilters, setOriginalPresetFilters] = useState<any>(null)
  const [originalPresetColumns, setOriginalPresetColumns] = useState<{
    visibility: Record<string, boolean>
    order: string[]
  } | null>(null)
  const [originalPresetSortBy, setOriginalPresetSortBy] = useState<Array<{
    sortBy: string
    direction: 'ASC' | 'DESC'
  }> | null>(null)
  const [originalPresetPageSize, setOriginalPresetPageSize] = useState<
    number | null
  >(null)
  const dispatchFilters = useFiltersDispatch()
  const [filtersLoadedOnPageLoad, setFiltersLoadedOnPageLoad] = useState(false)
  const dispatchPhoneAction = usePhoneDeviceDispatch()
  const phoneDeviceContext = usePhoneDevice()
  const isPageSizeInitialRender = useRef(true)
  const isLoadingPreset = useRef(false) // Track if we're loading a preset
  const pendingSaveFiltersRef = useRef<any>(null) // Track filters when explicitly saving
  const pendingSaveColumnsRef = useRef<{
    visibility: Record<string, boolean>
    order: string[]
  } | null>(null)
  const pendingSaveSortByRef = useRef<Array<{
    sortBy: string
    direction: 'ASC' | 'DESC'
  }> | null>(null)
  const pendingSavePageSizeRef = useRef<number | null>(null)
  const pendingDiscardRef = useRef(false) // Track if discard is in progress
  const hasLoadedPresetFromUrl = useRef(false) // Track if we've loaded preset from URL
  const filtersRef = useRef(null)
  const previousUrlParams = useRef<string | null>(null) // Track URL changes

  // Column visibility state - load from localStorage with defaults as fallback
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search)
      if (searchParams.get('viewId')) {
        return defaultColumnVisibility
      }
      try {
        const saved = localStorage.getItem('applicants-table-column-visibility')
        if (saved) {
          const savedVisibility = JSON.parse(saved)
          return { ...defaultColumnVisibility, ...savedVisibility }
        }
      } catch (error) {
        console.error(
          'Failed to load column visibility from localStorage:',
          error
        )
      }
    }
    return defaultColumnVisibility
  })

  // Column order state - use default order instead of localStorage
  const [columnOrder, setColumnOrder] = useState<string[]>(defaultColumnOrder)

  // Sort configuration state - default to application date descending
  const [sortByConfig, setSortByConfig] =
    useState<Array<{ sortBy: string; direction: 'ASC' | 'DESC' }>>(
      defaultSortByConfig
    )

  // Load custom fields for dynamic columns (get all fields, not paginated)
  const { data: customFieldsData } = useQuery(GET_CUSTOM_FIELDS, {
    variables: {
      status: 'ACTIVE',
      pageInfo: { limit: 100, offset: 0 }, // Get all fields (max 100)
    },
  })

  // Load employee permissions for permission-based UI
  const { data: permissionsData } = useQuery(GET_EMPLOYEE_PERMISSIONS, {
    variables: { employeeId: currentUser?.employeeId },
    skip: !currentUser?.employeeId,
  })

  const hasExportPermission = useMemo(() => {
    if (!permissionsData?.employeePermissions) return false
    return permissionsData.employeePermissions.some(
      (perm) =>
        perm.permissionKey === 'export:applicant_table_csv' && !perm.revokedAt
    )
  }, [permissionsData])

  const canDownloadCSV =
    currentUser?.roles?.includes('ADMIN') ||
    currentUser?.roles?.includes('RECRUITER') ||
    hasExportPermission

  // Fields available for filtering/sorting (backend only supports searching
  // select/boolean/date fields, so text fields are excluded here)
  const searchableCustomFields = useMemo(
    () =>
      (customFieldsData?.customFields?.items || []).filter(
        (f) => f.isSearchable
      ),
    [customFieldsData]
  )

  // Fields available for display as table columns — ALL active fields,
  // regardless of isSearchable, so free-text fields can be shown too
  const displayableCustomFields = useMemo(
    () =>
      (customFieldsData?.customFields?.items || []).filter(
        (f) => f.status === CustomFieldStatus.ACTIVE
      ),
    [customFieldsData]
  )

  // Generate custom field columns dynamically
  const customFieldColumns = useMemo(
    () => generateCustomFieldColumns(displayableCustomFields),
    [displayableCustomFields]
  )

  // Add custom field columns to columnOrder and columnVisibility when they're loaded
  useEffect(() => {
    if (displayableCustomFields.length > 0) {
      const customFieldKeys = displayableCustomFields.map(
        (f) => `customField_${f.customFieldId}`
      )

      // Add to columnOrder if not already present
      setColumnOrder((prev) => {
        const existingKeys = new Set(prev)
        const newKeys = customFieldKeys.filter((key) => !existingKeys.has(key))
        return newKeys.length > 0 ? [...prev, ...newKeys] : prev
      })

      // Add to columnVisibility if not already present (hidden by default)
      setColumnVisibility((prev) => {
        const updates = {}
        customFieldKeys.forEach((key) => {
          if (!(key in prev)) {
            updates[key] = false // Hidden by default
          }
        })
        return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev
      })

      // Keep originalPresetColumns in sync so new custom field keys don't
      // cause a false-positive hasUnsavedChanges = true (Bug 2 fix)
      setOriginalPresetColumns((prev) =>
        syncCustomFieldsToOriginalPreset(prev, customFieldKeys)
      )
    }
  }, [displayableCustomFields])

  // Generate step columns dynamically (enum step types + company custom steps)
  const customSteps = useMemo(
    () => customStepsCtx?.customSteps ?? [],
    [customStepsCtx?.customSteps]
  )

  const stepColumns = useMemo(
    () => generateStepColumns(undefined, customSteps),
    [customSteps]
  )

  // Add step columns to columnOrder and columnVisibility (hidden by default)
  useEffect(() => {
    if (stepColumns.length > 0) {
      const stepColumnKeys = stepColumns.map((col) => col.key)

      // Add to columnOrder if not already present
      setColumnOrder((prev) => {
        const existingKeys = new Set(prev)
        const newKeys = stepColumnKeys.filter((key) => !existingKeys.has(key))
        return newKeys.length > 0 ? [...prev, ...newKeys] : prev
      })

      // Add to columnVisibility if not already present (hidden by default)
      setColumnVisibility((prev) => {
        const updates = {}
        stepColumnKeys.forEach((key) => {
          if (!(key in prev)) {
            updates[key] = false // Hidden by default
          }
        })
        return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev
      })

      // Keep originalPresetColumns in sync so step column keys don't cause a
      // false-positive hasUnsavedChanges = true
      setOriginalPresetColumns((prev) =>
        syncCustomFieldsToOriginalPreset(prev, stepColumnKeys)
      )
    }
  }, [stepColumns])

  // The steps sub-query is scoped to what the table actually shows: active
  // steps (To Do column) plus any step types the user enabled as columns —
  // query weight scales with the number of step columns turned on.
  const stepFiltersVariable = useMemo(() => {
    const visibleStepColumnKeys = Object.keys(columnVisibility).filter(
      (key) => isStepColumn(key) && columnVisibility[key]
    )
    return buildStepFiltersVariable(visibleStepColumnKeys)
  }, [columnVisibility])

  // Save column visibility to localStorage whenever it changes (only when no preset is active)
  useEffect(() => {
    if (selectedPresetId) return
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(
          'applicants-table-column-visibility',
          JSON.stringify(columnVisibility)
        )
      } catch (error) {
        console.error(
          'Failed to save column visibility to localStorage:',
          error
        )
      }
    }
  }, [columnVisibility, selectedPresetId])

  // Column configuration for the display menu - ordered by columnOrder, excluding select column
  // Use useMemo to recalculate when columnOrder, columnVisibility, or custom fields change
  const columnConfigs = useMemo(
    () =>
      createColumnConfigs(
        columnOrder,
        columnVisibility,
        displayableCustomFields,
        stepColumns
      ),
    [columnOrder, columnVisibility, displayableCustomFields, stepColumns]
  )

  // Mutations
  const [createFilterPreset] = useMutation(CREATE_APPLICANT_FILTER_PRESET, {
    onCompleted: (data) => {
      const newPresetId =
        data.createApplicantFilterPreset.applicantFilterPresetId
      // Set the new preset as selected
      setSelectedPresetId(newPresetId)

      // Update URL to use viewId instead of view
      const searchParams = new URLSearchParams(window.location.search)
      searchParams.set('viewId', newPresetId)
      searchParams.delete('view') // Remove default view param
      navigate(`?${searchParams.toString()}`, { replace: true })

      toast('View created', 'success')
      refetchFilterPresets()
    },
    onError: () => {
      toast('Failed to create view', 'error')
    },
  })

  const [updateFilterPreset] = useMutation(UPDATE_APPLICANT_FILTER_PRESET, {
    onCompleted: () => {
      toast('View updated', 'success')
      refetchFilterPresets() // Refetch to get updated data
      // Update originals only when explicitly saving (not auto-save)
      if (pendingSaveFiltersRef.current) {
        setOriginalPresetFilters(cloneDeep(pendingSaveFiltersRef.current))
        pendingSaveFiltersRef.current = null
      }
      if (pendingSaveColumnsRef.current) {
        setOriginalPresetColumns(cloneDeep(pendingSaveColumnsRef.current))
        pendingSaveColumnsRef.current = null
      }
      if (pendingSaveSortByRef.current) {
        setOriginalPresetSortBy(cloneDeep(pendingSaveSortByRef.current))
        pendingSaveSortByRef.current = null
      }
      if (pendingSavePageSizeRef.current) {
        setOriginalPresetPageSize(pendingSavePageSizeRef.current)
        pendingSavePageSizeRef.current = null
      }
    },
    onError: () => {
      toast('Failed to update view', 'error')
      pendingSaveFiltersRef.current = null
      pendingSaveColumnsRef.current = null
      pendingSaveSortByRef.current = null
      pendingSavePageSizeRef.current = null
    },
  })

  const handleColumnVisibilityChange = (
    columnKey: string,
    visible: boolean
  ) => {
    const newVisibility = {
      ...columnVisibility,
      [columnKey]: visible,
    }
    setColumnVisibility(newVisibility)
    // Note: Auto-save has been disabled. Users must explicitly save changes to presets.
  }

  const handleColumnReorder = (newOrder: string[]) => {
    // Always ensure select column is first
    const fullOrder = ['select', ...newOrder]
    setColumnOrder(fullOrder)
    // Note: Auto-save has been disabled. Users must explicitly save changes to presets.
  }

  const filters = useFilters()

  const handleSortConfigChange = useCallback((newSortConfig) => {
    setSortByConfig(newSortConfig)
    // Note: Auto-save has been disabled. Users must explicitly save changes to presets.
  }, [])

  // Clicking a sortable column header replaces the sort with a single field,
  // toggling direction. Mirrors the SortByMenu config shape.
  const handleHeaderSortChange = useCallback(
    (field: string, direction: 'asc' | 'desc') => {
      setSortByConfig([
        { sortBy: field, direction: direction === 'desc' ? 'DESC' : 'ASC' },
      ])
    },
    []
  )

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data: filterPresetData, refetch: refetchFilterPresets } = useQuery(
    GET_APPLICANT_FILTER_PRESETS,
    {
      variables: {
        employeeId: currentUser?.employeeId,
        pageInfo: { limit: 1000, offset: 0 },
      },
    }
  )

  const filterPresets = useMemo(
    () => filterPresetData?.applicantFilterPresets?.items ?? [],
    [filterPresetData?.applicantFilterPresets?.items]
  )

  // Use conditional data loading hook
  const { loadRequiredData, loadedOptionsRef } = useConditionalDataLoading({
    onError,
  })

  const init = useCallback(async () => {
    // Store current URL params to detect external navigation changes
    previousUrlParams.current = window.location.search

    // Load default filters immediately to allow applicants query to run
    const urlFilters = loadFiltersFromUrl({
      jobListings: [],
      recruiters: [],
      tags: [],
      customFields: searchableCustomFields,
    })
    searchTextSet(urlFilters.searchText.searchText)

    dispatchFilters({
      type: ApplicantFiltersActionType.FILTERS_CHANGED,
      payload: urlFilters,
    })
    setFiltersLoadedOnPageLoad(true)

    // Only load data that's actually needed from URL parameters
    const requiredDataTypes = getRequiredDataTypesFromUrl()

    try {
      await loadRequiredData(requiredDataTypes)

      // Re-load filters with the loaded data to populate options
      const urlFiltersWithData = loadFiltersFromUrl({
        jobListings: loadedOptionsRef.current.jobListings,
        recruiters: loadedOptionsRef.current.recruiters,
        tags: loadedOptionsRef.current.tags,
        customFields: searchableCustomFields,
      })

      searchTextSet(urlFiltersWithData.searchText.searchText)
      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: urlFiltersWithData,
      })
    } catch (error) {
      console.error('Error loading initial data:', error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRequiredData, searchableCustomFields])

  useEffect(() => {
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Track previous custom fields length to detect when they become available
  const prevCustomFieldsLengthRef = useRef(0)

  // Re-parse URL filters when custom fields become available
  // This handles the case where custom fields load after initial page load
  // and ensures SINGLE_SELECT values are correctly parsed as selectValues instead of stringValue
  useEffect(() => {
    // Check if custom fields just became available (transition from empty to non-empty)
    const customFieldsJustLoaded =
      prevCustomFieldsLengthRef.current === 0 &&
      searchableCustomFields.length > 0

    // Update the ref for next comparison
    prevCustomFieldsLengthRef.current = searchableCustomFields.length

    // Re-parse if:
    // 1. Custom fields just became available (were empty, now have data)
    // 2. There are custom field URL parameters that need parsing
    // 3. Filters were already loaded (to avoid double-parsing during init)
    if (
      customFieldsJustLoaded &&
      filtersLoadedOnPageLoad &&
      typeof window !== 'undefined'
    ) {
      const searchParams = new URLSearchParams(window.location.search)
      const hasCustomFieldParams = Array.from(searchParams.keys()).some((key) =>
        key.startsWith('cf_')
      )

      if (hasCustomFieldParams) {
        // Re-load filters with the now-available custom fields metadata
        // This ensures SINGLE_SELECT values are correctly parsed as selectValues
        const urlFiltersWithCustomFields = loadFiltersFromUrl({
          jobListings: loadedOptionsRef.current.jobListings,
          recruiters: loadedOptionsRef.current.recruiters,
          tags: loadedOptionsRef.current.tags,
          customFields: searchableCustomFields,
        })

        searchTextSet(urlFiltersWithCustomFields.searchText.searchText)
        dispatchFilters({
          type: ApplicantFiltersActionType.FILTERS_CHANGED,
          payload: urlFiltersWithCustomFields,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchableCustomFields, filtersLoadedOnPageLoad])

  // Detect when URL changes externally (e.g., navigation from Dashboard)
  useEffect(() => {
    const currentUrlParams = location.search

    if (previousUrlParams.current === null) {
      // First time, just store the URL
      previousUrlParams.current = currentUrlParams
      return
    }

    // Parse both URLs to compare filter params (excluding internal nav params)
    const previousParams = new URLSearchParams(previousUrlParams.current)
    const currentParams = new URLSearchParams(currentUrlParams)

    // Remove internal navigation params that shouldn't trigger a reload
    const internalParams = ['page', 'viewId', 'view']
    internalParams.forEach((param) => {
      previousParams.delete(param)
      currentParams.delete(param)
    })

    // Check if filter params actually changed
    const filterParamsChanged =
      previousParams.toString() !== currentParams.toString()

    // If filter params changed and it's not the initial load, reload filters
    // BUT: don't reload if we're currently loading a preset (internal navigation)
    if (
      filterParamsChanged &&
      filtersLoadedOnPageLoad &&
      !isLoadingPreset.current // Don't interfere with preset loading
    ) {
      previousUrlParams.current = currentUrlParams

      // Reload filters from the new URL
      init()
    } else if (isLoadingPreset.current || !filterParamsChanged) {
      // If loading a preset or only internal params changed, just update the tracked URL
      previousUrlParams.current = currentUrlParams
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  const [searchText, searchTextSet] = useState('')

  // Keep filters ref updated
  useEffect(() => {
    filtersRef.current = filters
  }, [filters])

  const [loadApplicants, { data: applicantsPage, loading, refetch }] =
    useLazyQuery(
    GET_APPLICANTS,
    {
      fetchPolicy: 'no-cache',
      variables: {
        pageInfo: {
          offset: (page - 1) * pageSize,
          limit: pageSize,
        },
        filters: buildFiltersGraphqlParam(filters),
        sortBy: transformSortConfigForGraphQL(sortByConfig),
        stepFilters: stepFiltersVariable,
      },
      onError,
    }
  )

  useEffect(() => {
    const applicants =
      applicantsPage?.applicants?.items.map(
        (applicant) => applicant.applicantId
      ) || []
    setApplicantList(applicants)
  }, [applicantsPage, setApplicantList])

  const [downloadCSV] = useMutation(DOWNLOAD_CSV, {
    variables: {
      filters: buildFiltersGraphqlParam(filters),
      pageInfo: { offset: 0, limit: LIMIT_CSV },
      sortBy: transformSortConfigForGraphQL(sortByConfig),
    },
    onCompleted: (result) => {
      if (result?.downloadCSV?.fileContent) {
        const fileAsBinary = base64ToBlob({
          base64String: result.downloadCSV.fileContent,
          contentType: 'text/csv',
        })
        downloadFileBlob({
          file: fileAsBinary,
          fileName: result.downloadCSV.fileName,
        })
      }
    },
    onError,
  })

  // Load preset from URL on page load (only once)
  useEffect(() => {
    if (
      !filtersLoadedOnPageLoad ||
      !filterPresets ||
      hasLoadedPresetFromUrl.current
    )
      return

    const searchParams = new URLSearchParams(window.location.search)
    const viewId = searchParams.get('viewId')
    const viewName = searchParams.get('view')

    if (viewId) {
      // Load custom preset by ID
      const preset = filterPresets.find(
        (p) => p.applicantFilterPresetId === viewId
      )
      if (preset) {
        hasLoadedPresetFromUrl.current = true
        handlePresetClick(preset, true)
      }
    } else if (viewName) {
      // Load default preset by name from hardcoded stages
      const defaultPreset = defaultHiringStages.find((p) => p.name === viewName)
      if (defaultPreset) {
        hasLoadedPresetFromUrl.current = true
        handlePresetClick(defaultPreset, false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersLoadedOnPageLoad, filterPresets])

  // Fetch only when the request itself changes. The filter state also holds
  // UI data (option lists, labels) whose updates must not refetch the table.
  const applicantsVariables = useMemo(
    () => ({
      pageInfo: {
        offset: (page - 1) * pageSize,
        limit: pageSize,
      },
      filters: buildFiltersGraphqlParam(filters),
      sortBy: transformSortConfigForGraphQL(sortByConfig),
      stepFilters: stepFiltersVariable,
    }),
    [filters, page, pageSize, sortByConfig, stepFiltersVariable]
  )
  const applicantsVariablesKey = JSON.stringify(applicantsVariables)

  useEffect(() => {
    loadApplicants({ variables: applicantsVariables })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantsVariablesKey])

  // Sidebar edits bump refreshKey: refetch in place so the table keeps its
  // rows and scroll instead of swapping to the loading skeleton
  const lastRefreshKey = useRef(refreshKey)
  useEffect(() => {
    if (refreshKey === lastRefreshKey.current) return
    lastRefreshKey.current = refreshKey
    refetch?.()
  }, [refreshKey, refetch])

  useEffect(() => {
    // We should only run this effect if the page size is changed, not on initial render
    if (isPageSizeInitialRender.current) {
      isPageSizeInitialRender.current = false
      return
    }

    setPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize])

  // Handle filter changes that might require additional data loading
  const handleFilterChange = useCallback(
    async (newFilters) => {
      const requiredDataTypes = getRequiredDataTypes(newFilters)

      try {
        await loadRequiredData(requiredDataTypes)
      } catch (error) {
        console.error('Error loading filter data:', error)
      }
    },
    [loadRequiredData]
  )

  useEffect(() => {
    // Update UI elements when filters change
    setSelectedFilters(
      buildSelectedFilters(
        filters,
        searchableCustomFields,
        customStepsCtx?.labelById ?? {}
      )
    )
    setSelectedApplicants([])

    // Sync filters to URL and load any additional data needed for new filters.
    // Gated behind filtersLoadedOnPageLoad to avoid redundant work during init
    // (init() already handles its own data loading).
    if (filtersLoadedOnPageLoad) {
      updateFiltersInUrl(filters)
      handleFilterChange(filters)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchableCustomFields, customStepsCtx?.labelById])

  // Stable callback for ApplicantFilterMenuWrapper to avoid re-render cascade
  const handleFilterMenuPageReset = useCallback(() => setPage(1), [setPage])

  const onSearch = (text: string) => {
    dispatchFilters({
      type: ApplicantFiltersActionType.SEARCHBOX_CHANGED,
      payload: {
        text,
      },
    })
    setPage(1)
  }
  const handleRemoveSelectedFilter = (filter: SelectedFilter) => {
    onRemoveSelectedFilter(filter, {
      dispatchFilters,
      searchTextSet,
      setSelectedPresetId,
      setSelectedPresetFilter,
      selectedPresetFilter,
    })
    setPage(1)
  }

  const handleReset = () => {
    setOriginalPresetFilters(null)
    setOriginalPresetPageSize(null)
    reset(
      setSelectedPresetId,
      setSelectedPresetFilter,
      dispatchFilters,
      navigate
    )
    setPage(1)
  }

  const handlePresetClick = async (item, isCustomPreset = false) => {
    // Clear stale localStorage so it doesn't interfere with preset column loading
    localStorage.removeItem('applicants-table-column-visibility')

    // Determine what data types are required for this preset
    const requiredDataTypes = getPresetRequiredDataTypes(item, isCustomPreset)

    // Load only the required data
    try {
      await loadRequiredData(requiredDataTypes)
    } catch (error) {
      console.error('Error loading preset data:', error)
      return
    }

    // Store original filters for custom presets so we can detect unsaved changes
    // Transform filters with options first so comparison works correctly
    if (isCustomPreset && item.filters) {
      const transformedFilters = mergeOptionsIntoFilters(
        item.filters,
        loadedOptionsRef
      )
      setOriginalPresetFilters(cloneDeep(transformedFilters))
      setOriginalPresetPageSize(getPresetPageSize(item) ?? DEFAULT_PAGE_SIZE)
    } else {
      setOriginalPresetFilters(null)
      setOriginalPresetPageSize(null)
    }

    // Track column/sort state that the preset will apply, for unsaved changes detection
    const capturedColumnVisibility: Record<string, boolean> = {}
    const capturedColumnOrder: string[] = []
    const captureColumnVisibility = (v: Record<string, boolean>) => {
      Object.assign(capturedColumnVisibility, v)
      setColumnVisibility(v)
    }
    const captureColumnOrder = (o: string[]) => {
      capturedColumnOrder.push(...o)
      setColumnOrder(o)
    }

    onPresetClick(item, isCustomPreset, {
      isLoadingPreset,
      mergeOptionsIntoFilters: (filters) =>
        mergeOptionsIntoFilters(filters, loadedOptionsRef),
      dispatchFilters,
      searchTextSet,
      setSelectedPresetId,
      setSelectedPresetFilter,
      loadColumnsFromPreset: (columns) =>
        loadColumnsFromPreset(
          columns,
          columnVisibility,
          captureColumnVisibility,
          captureColumnOrder
        ),
      setSortByConfig,
      navigate,
    })

    // Store original column/sort state for unsaved changes detection
    if (isCustomPreset) {
      setOriginalPresetColumns({
        visibility: cloneDeep(capturedColumnVisibility),
        order: [...capturedColumnOrder],
      })
      // Capture the sort config that onPresetClick applied
      // Must reconstruct CUSTOM_FIELD_<id> format to match sortByConfig
      if (item.sortBy && item.sortBy.length > 0) {
        const sortedSortBy = [...item.sortBy].sort(
          (a, b) => (a.order || 0) - (b.order || 0)
        )
        setOriginalPresetSortBy(
          sortedSortBy.map((s) => ({
            sortBy:
              s.sortBy === 'CUSTOM_FIELD' && s.customFieldId
                ? `CUSTOM_FIELD_${s.customFieldId}`
                : s.sortBy,
            direction: s.direction as 'ASC' | 'DESC',
          }))
        )
      } else {
        setOriginalPresetSortBy([
          { sortBy: 'APPLICATION_DATE', direction: 'DESC' },
        ])
      }
    } else {
      setOriginalPresetColumns(null)
      setOriginalPresetSortBy(null)
    }
  }

  const onDownloadCSV = useCallback(() => {
    downloadCSV()
  }, [downloadCSV])

  const handleAutoUpdatePresetFilters = useCallback(() => {
    autoUpdatePresetFilters(
      selectedPresetId,
      columnOrder,
      columnVisibility,
      sortByConfig,
      filters,
      pageSize,
      ensureCompleteFilterStructure,
      updateFilterPreset
    )
  }, [
    selectedPresetId,
    columnOrder,
    columnVisibility,
    sortByConfig,
    filters,
    pageSize,
    updateFilterPreset,
  ])

  const handleCreateCustomPresetFromFilters = useCallback(() => {
    createCustomPresetFromFilters(
      selectedPresetFilter,
      filterPresets,
      columnOrder,
      columnVisibility,
      sortByConfig,
      pageSize,
      filtersRef,
      ensureCompleteFilterStructure,
      createFilterPreset,
      setSelectedPresetId,
      navigate,
      handleAutoUpdatePresetFilters
    )
  }, [
    selectedPresetFilter,
    filterPresets,
    columnOrder,
    columnVisibility,
    sortByConfig,
    pageSize,
    createFilterPreset,
    handleAutoUpdatePresetFilters,
  ])

  // Check if current filters, columns, or sort differ from original preset
  const hasUnsavedChanges = useMemo(
    () =>
      computeHasUnsavedChanges({
        selectedPresetId,
        originalPresetFilters,
        filters,
        originalPresetColumns,
        columnVisibility,
        columnOrder,
        originalPresetSortBy,
        sortByConfig,
        originalPresetPageSize,
        pageSize,
      }),
    [
      selectedPresetId,
      originalPresetFilters,
      filters,
      originalPresetColumns,
      columnVisibility,
      columnOrder,
      originalPresetSortBy,
      sortByConfig,
      originalPresetPageSize,
      pageSize,
    ]
  )

  // Handler to explicitly save changes to the current preset
  const handleSavePresetChanges = useCallback(() => {
    if (!selectedPresetId) return

    // Store filters in ref so onCompleted can update originalPresetFilters
    pendingSaveFiltersRef.current = filters
    pendingSaveColumnsRef.current = {
      visibility: cloneDeep(columnVisibility),
      order: [...columnOrder],
    }
    pendingSaveSortByRef.current = cloneDeep(sortByConfig)
    pendingSavePageSizeRef.current = pageSize
    handleAutoUpdatePresetFilters()
  }, [
    selectedPresetId,
    handleAutoUpdatePresetFilters,
    filters,
    columnVisibility,
    columnOrder,
    sortByConfig,
    pageSize,
  ])

  // Handler to discard changes and revert to original preset
  const handleDiscardChanges = useCallback(() => {
    pendingDiscardRef.current = true

    // Restore original filters
    if (originalPresetFilters) {
      dispatchFilters({
        type: ApplicantFiltersActionType.FILTERS_CHANGED,
        payload: cloneDeep(originalPresetFilters),
      })
      searchTextSet(originalPresetFilters.searchText?.searchText || '')
    }

    // Restore original columns
    if (originalPresetColumns) {
      setColumnVisibility(cloneDeep(originalPresetColumns.visibility))
      setColumnOrder([...originalPresetColumns.order])
    }

    // Restore original sort
    if (originalPresetSortBy) {
      setSortByConfig(cloneDeep(originalPresetSortBy))
    }

    if (originalPresetPageSize) {
      setPageSize(originalPresetPageSize)
    }

    setPage(1)
  }, [
    originalPresetFilters,
    originalPresetColumns,
    originalPresetSortBy,
    originalPresetPageSize,
    dispatchFilters,
    searchTextSet,
    setPageSize,
  ])

  // After a discard, re-sync originals from actual state
  // so the JSON comparison in hasUnsavedChanges matches exactly
  useEffect(() => {
    if (pendingDiscardRef.current) {
      pendingDiscardRef.current = false
      setOriginalPresetFilters(cloneDeep(filters))
      setOriginalPresetColumns({
        visibility: cloneDeep(columnVisibility),
        order: [...columnOrder],
      })
      setOriginalPresetSortBy(cloneDeep(sortByConfig))
      setOriginalPresetPageSize(pageSize)
    }
  }, [filters, columnVisibility, columnOrder, sortByConfig, pageSize])

  const onPhoneCall = useCallback(
    async ({ applicantId, applicantFullName, phone }) => {
      if (phoneDeviceContext?.phoneDevice?.isBusy) {
        return
      }
      dispatchPhoneAction({
        type: PhoneDeviceActionType.CALL,
        payload: {
          applicantId,
          phone,
          applicantFullName,
          // call,
        },
      })
    },
    [phoneDeviceContext?.phoneDevice?.isBusy, dispatchPhoneAction]
  )

  //This tracks and saves the most recent url with filtered applicants
  //so that it can be used to redirect back to the same page after seeing an applicant's details
  useEffect(() => {
    localStorage.setItem(
      'latestUrlWithFilteredApplicants',
      window.location.href
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.href])

  return (
    <div className="flex h-full w-full flex-col gap-3">
      {filterPresetModalOpened && (
        <FilterPresetModal
          refetch={refetchFilterPresets}
          filters={filters}
          opened={filterPresetModalOpened}
          columnVisibility={columnVisibility}
          columnOrder={columnOrder}
          sortBy={sortByConfig}
          pageSize={pageSize}
          existingPresetNames={filterPresets.map((preset) => preset.name)}
          onClose={(viewName, viewId) => {
            if (viewName && viewId) {
              setSelectedPresetFilter(viewName)
              setSelectedPresetId(viewId)

              // Capture current state as the baseline so subsequent changes to
              // columns, sort, or filters are correctly detected as unsaved (Bug 3 fix)
              setOriginalPresetFilters(cloneDeep(filters))
              setOriginalPresetColumns({
                visibility: cloneDeep(columnVisibility),
                order: [...columnOrder],
              })
              setOriginalPresetSortBy(cloneDeep(sortByConfig))
              setOriginalPresetPageSize(pageSize)

              // Update URL to use the new viewId
              const searchParams = new URLSearchParams(window.location.search)
              searchParams.set('viewId', viewId)
              searchParams.delete('view') // Remove default view param if present
              navigate(`?${searchParams.toString()}`, { replace: true })
            }
            filterPresetModalHandlers.close()
          }}
        />
      )}
      {opened && <CreateApplicantDrawer opened={opened} close={close} />}
      <Metadata title="Applicants" description="Applicants page" />
      <BulkActionsDrawer
        opened={bulkActionsDrawerOpened}
        onClose={bulkActionsDrawerHandlers.close}
        selectedApplicants={selectedApplicants}
        setSelectedApplicants={setSelectedApplicants}
        onSelectAction={(actionName) => {
          setActiveBulkActionName(actionName)
        }}
        activeAction={activeBulkActionName}
      />
      {canScheduleAutomations && (
        <ScheduleAutomationsModal
          opened={scheduleAutomationsModalOpened}
          onClose={scheduleAutomationsModalHandlers.close}
        />
      )}

      <div className="flex h-full flex-col gap-3 px-5 pb-3">
        <div className="flex flex-row items-center gap-2">
          <FilterPresetList
            defaultPresets={defaultHiringStages}
            customPresets={filterPresets}
            onPresetClick={(item, isCustomPreset) => {
              handlePresetClick(item, isCustomPreset)
              setPage(1)
            }}
            reset={handleReset}
            selectedPresetFilter={selectedPresetFilter}
            numberOfApplicants={
              applicantsPage?.applicants.totalCount >= 0
                ? applicantsPage?.applicants.totalCount
                : undefined
            }
            onAddNewView={filterPresetModalHandlers.open}
            refetch={refetchFilterPresets}
            hasUnsavedChanges={hasUnsavedChanges}
            onSaveChanges={handleSavePresetChanges}
            onDiscardChanges={handleDiscardChanges}
          />
        </div>

        <div className="flex flex-row items-center gap-2">
          {selectedApplicants.length > 0 && (
            <Tag
              variant="secondary"
              className="shrink-0 normal-case"
              onClose={() => setSelectedApplicants([])}
            >
              {selectedApplicants.length}{' '}
              {selectedApplicants.length === 1 ? 'applicant' : 'applicants'}{' '}
              selected
            </Tag>
          )}
          <SelectedFiltersBar
            selectedFilters={selectedFilters}
            onRemoveFilter={handleRemoveSelectedFilter}
          />
          <div className="ml-auto flex shrink-0 flex-row items-center gap-2">
            <CollapsibleSearchBox
              value={searchText}
              setValue={searchTextSet}
              appliedValue={filters.searchText?.searchText ?? ''}
              placeholder="Search by name, phone number, or email"
              onSearch={onSearch}
            />
            <ApplicantFilterMenuWrapper
              onFilterChange={handleFilterMenuPageReset}
            />
            <ColumnDisplayMenu
              key={`display-${selectedPresetId || selectedPresetFilter}`}
              columns={columnConfigs}
              onColumnVisibilityChange={handleColumnVisibilityChange}
              onColumnReorder={handleColumnReorder}
            />
            <SortByMenu
              key={`sort-${selectedPresetId || selectedPresetFilter}`}
              sortConfig={sortByConfig}
              onSortConfigChange={handleSortConfigChange}
              customFields={searchableCustomFields}
            />
            <Menu
              position="bottom-end"
              offset={3}
              withArrow
              shadow="md"
              width={200}
            >
              <Menu.Target>
                <DNButton type="button">
                  <IconChevronDown className="h-4 w-4 fill-none stroke-doubleNickel-gray-700" />
                  Actions
                </DNButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>All Actions</Menu.Label>
                <Menu.Item
                  className="text-sm"
                  onClick={onDownloadCSV}
                  disabled={!canDownloadCSV}
                >
                  Download CSV
                </Menu.Item>
                <Menu.Item
                  className="text-sm"
                  onClick={() => {
                    bulkActionsDrawerHandlers.open()
                  }}
                >
                  Bulk Actions
                </Menu.Item>
                {canScheduleAutomations && (
                  <Menu.Item
                    className="text-sm"
                    onClick={() => {
                      scheduleAutomationsModalHandlers.open()
                    }}
                  >
                    Schedule Automations (Internal)
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
            <DNButton variant="primary" onClick={() => open()}>
              <Plus className="h-4 fill-none stroke-doubleNickel-white" />
              Add Applicant
            </DNButton>
          </div>
        </div>

        <div className="h-full overflow-auto">
          <ApplicantsTable
            applicantPage={applicantsPage?.applicants}
            selectedApplicants={selectedApplicants}
            setSelectedApplicants={setSelectedApplicants}
            openDrawer={handleOpen}
            loading={loading}
            onPhoneCall={onPhoneCall}
            columnVisibility={columnVisibility}
            columnOrder={columnOrder}
            customFieldColumns={customFieldColumns}
            stepColumns={stepColumns}
            sortField={sortByConfig.length === 1 ? sortByConfig[0].sortBy : ''}
            sortDirection={
              sortByConfig.length === 1 && sortByConfig[0].direction === 'DESC'
                ? 'desc'
                : 'asc'
            }
            onSortChange={handleHeaderSortChange}
          />
        </div>
        <PaginationArea
          page={page}
          resultsPerPage={pageSize}
          setResultsPerPage={setPageSize}
          totalPages={
            applicantsPage?.applicants.totalCount >= 0
              ? Math.ceil(applicantsPage?.applicants.totalCount / pageSize)
              : page + 1
          }
          onPageChange={setPage}
        />
      </div>
    </div>
  )
}

export default ApplicantsPage
